import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  NavigateOptions,
  To,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { DrawerContextProvider } from "./DrawerContext";
import { ToastContextProvider } from "./ToastContext";
import {
  OrganizationPreferences,
  SubscribedRegulationGroup,
  SubscriptionStatus,
  SubscriptionTier,
  UnitPriceUnit,
  User,
  UserProgress,
} from "../api/public";
import { useAddToPantryMutation } from "../hooks/useAddToPantryMutation";
import { useGetFeatures } from "../hooks/useGetFeatures";
import { useGetMe } from "../hooks/useGetMe";
import { useGetUserProgress } from "../hooks/useGetUserProgress";
import { useRemoveFromPantryMutation } from "../hooks/useRemoveFromPantryMutation";
import { useUserPantryIdsQuery } from "../hooks/useUserPantryIdsQuery";

type PreviousEntry = {
  title?: string;
};

type ExpandedNavigationOptions = NavigateOptions & {
  title?: string;
  currentTitle?: string;
};

export type CustomNavigationFunction = (
  to: To,
  options?: ExpandedNavigationOptions
) => void;

type AppContextType = {
  me?: User;
  activeOrganizationId?: string;
  orgPreferences?: OrganizationPreferences;
  subscribedRegulationGroups: SubscribedRegulationGroup[];
  tier?: SubscriptionTier;
  isFreeTrial: boolean;
  getDefaultPriceUnit: (value?: string) => UnitPriceUnit;
  pantryIds: Set<string>;
  addToPantry: (rawMaterialId: string) => void;
  removeFromPantry: (rawMaterialId: string) => void;
  features: Set<string>;
  progress?: UserProgress;
  backText?: string;
  backAvailable?: boolean;
  navigateWithStack: (to: To, options?: ExpandedNavigationOptions) => void;
  navigateBack: () => void;
};

export const AppContext = createContext<AppContextType>({
  pantryIds: new Set<string>(),
  getDefaultPriceUnit: () => UnitPriceUnit.KG,
  addToPantry: () => {},
  removeFromPantry: () => {},
  features: new Set<string>(),
  backText: undefined,
  backAvailable: false,
  isFreeTrial: false,
  subscribedRegulationGroups: [],
  navigateWithStack: () => {},
  navigateBack: () => {},
});

export const AppContextProvider = ({ children }: PropsWithChildren) => {
  const { data: me } = useGetMe({
    refetchInterval: 15000,
    refetchOnWindowFocus: "always",
  });
  const { data: features } = useGetFeatures();
  const { data: pantryIds } = useUserPantryIdsQuery();
  const { mutate: add } = useAddToPantryMutation();
  const { mutate: remove } = useRemoveFromPantryMutation();
  const { data: progress } = useGetUserProgress();

  const [activeOrganizationId, setActiveOrganizationId] = useState<
    string | undefined
  >(undefined);

  const navigate = useNavigate();
  const location = useLocation();

  const navigateWithStack = useCallback(
    (to: To, options?: ExpandedNavigationOptions) => {
      const { currentTitle, ...navigateOptions } = options ?? {};
      const currentState = location.state as { previous?: PreviousEntry };
      const state = {
        ...(navigateOptions.state ?? {}),
        previous: {
          title: navigateOptions.replace
            ? currentState?.previous?.title
            : currentTitle,
        },
      };
      navigate(to, { ...navigateOptions, state });
    },
    [location.state, navigate]
  );

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const backText = useMemo(() => {
    const state = location.state as { previous?: PreviousEntry };
    return state?.previous?.title ? `Back to ${state.previous.title}` : "Back";
  }, [location.state]);

  const backAvailable = useMemo(() => {
    const state = location.state as { previous?: PreviousEntry };
    return state?.previous !== undefined;
  }, [location.state]);

  useEffect(() => {
    setActiveOrganizationId((prev) => {
      const orgs = me?.organizations ?? [];
      const org = orgs.find((o) => o.id === prev);
      if (org) {
        return prev;
      }
      if (orgs.length > 0) {
        return orgs[0].id;
      }
      return undefined;
    });
  }, [me]);

  const activeOrganization = useMemo(() => {
    const orgs = me?.organizations ?? [];
    return orgs.find((o) => o.id === activeOrganizationId);
  }, [me, activeOrganizationId]);

  const orgPreferences = useMemo(() => {
    return activeOrganization?.preferences;
  }, [activeOrganization?.preferences]);

  const subscribedRegulationGroups = useMemo(() => {
    return activeOrganization?.subscribed_regulation_groups ?? [];
  }, [activeOrganization?.subscribed_regulation_groups]);

  const tier = useMemo(() => {
    const orgs = me?.organizations ?? [];
    const org = orgs.find((o) => o.id === activeOrganizationId);
    return org?.tier;
  }, [me, activeOrganizationId]);

  const isFreeTrial = useMemo(() => {
    const orgs = me?.organizations ?? [];
    const org = orgs.find((o) => o.id === activeOrganizationId);
    if (!org) return false;

    return (
      !!org.is_free_trial &&
      !!(org.tier === SubscriptionTier.BASIC) &&
      !!(org.subscription_status === SubscriptionStatus.ACTIVE)
    );
  }, [me, activeOrganizationId]);

  const getDefaultPriceUnit = useCallback(
    (value?: string) => {
      if (value) {
        return value as UnitPriceUnit;
      }

      const preferredUnit = orgPreferences?.mass_unit;
      if (preferredUnit === OrganizationPreferences.mass_unit.OZ)
        return UnitPriceUnit.OZ;
      if (preferredUnit === OrganizationPreferences.mass_unit.KG)
        return UnitPriceUnit.KG;

      return UnitPriceUnit.OZ;
    },
    [orgPreferences?.mass_unit]
  );

  const addToPantry = useCallback(
    (rawMaterialId: string) => {
      return add({ rawMaterialId, organizationId: activeOrganizationId });
    },
    [add, activeOrganizationId]
  );

  const appContextValue = useMemo(() => {
    return {
      me,
      activeOrganizationId,
      orgPreferences,
      subscribedRegulationGroups,
      tier,
      isFreeTrial,
      getDefaultPriceUnit,
      pantryIds: new Set(pantryIds ?? []),
      addToPantry,
      removeFromPantry: remove,
      features: new Set(features?.map((f) => f.name) ?? []),
      progress,
      backText,
      backAvailable,
      navigateWithStack,
      navigateBack,
    };
  }, [
    me,
    activeOrganizationId,
    orgPreferences,
    subscribedRegulationGroups,
    tier,
    isFreeTrial,
    getDefaultPriceUnit,
    pantryIds,
    addToPantry,
    remove,
    features,
    progress,
    backText,
    backAvailable,
    navigateWithStack,
    navigateBack,
  ]);

  return (
    <AppContext.Provider value={appContextValue}>
      <DrawerContextProvider>
        <ToastContextProvider>{children}</ToastContextProvider>
      </DrawerContextProvider>
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
};
